import {
  Box,
  Flex,
  Heading,
  Image,
  Link,
  List,
  ListItem,
  Text,
  useColorMode,
} from "@chakra-ui/react";
import {
  CIcon,
  FacebookIcon,
  InstagramIcon,
  TwitterIcon,
  YouTubeIcon,
} from "../../assets/icons/icons";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useFacetMeta } from "../../hooks/useFacetMeta";
import { generateSeoCategoryLink } from "../../constants";
import emailContactDark from "../../assets/image/email-contact-dark.png";
import emailContact from "../../assets/image/email-contact.png";
import { getCurrentColor } from "../../constants";
import logo from "../../assets/image/logo-light.png";

const quickLinks = [
  {
    name: "Home",
    link: "/",
  },
  {
    name: "About",
    link: "/about-us",
  },
  {
    name: "FAQs",
    link: "/faq",
  },
  {
    name: "Contact Us",
    link: "/contact-us",
  },
  {
    name: "Retailers",
    link: "/registration",
  },
];

const typeOfAmmo = [
  { name: "Handgun Ammo", link: "/handgun-ammo" },
  { name: "Rifle Ammo", link: "/rifle-ammo" },
  { name: "Shotgun Ammo", link: "/shotgun-ammo" },
  { name: "Rimfire Ammo", link: "/rimfire-ammo" },
];

const Footer = () => {
  const { colorMode } = useColorMode();
  const { facetMappings, mappingsReady } = useFacetMeta();

  const location = useLocation();

  return (
    <Box
      bg={getCurrentColor(colorMode, "blueC.600", "grayC.1700")}
      pb={{ base: "57px", lg: 0 }}
    >
      <Flex
        maxW={"1240px"}
        w={"100%"}
        mx={"auto"}
        px={{ base: "22px", xl: "10px" }}
        justifyContent={{ base: "center", xl: "space-between" }}
        flexDirection={{ base: "column", md: "row" }}
        flexWrap={"wrap"}
        gap={5}
        py={10}
      >
        <Flex
          direction={"column"}
          maxW={{ base: "auto", lg: "360px" }}
          w={{ base: "100%", md: "48%", lg: "auto" }}
          mb={{ base: 10, md: 0 }}
        >
          <Heading
            as="h2"
            fontSize={"22px"}
            color={"whiteC.100"}
            fontFamily={"mainFont"}
            fontWeight={"600"}
            mb={3}
          >
            <Image
              maxW={"108px"}
              // src="https://cdn.bulletscout.com/image/logo-light.png"
              src={logo}
              alt="logo footer"
              w={"100%"}
              h={"100%"}
            />
          </Heading>
          <Text mb={5} color={"whiteC.100"} fontSize={"sm"}>
            The new premier ammo search engine for finding the actual cheapest
            ammunition prices online in 2025. Shop for rifle, pistol, rimfire,
            and shotgun ammo in bulk or in small quantities, with shipping
            prices included. Browse for products using our innovative
            product-centric search feature and compare historical pricing data
            for past trends.
          </Text>
          <Flex gap={4} alignItems={"flex-end"} mt={"auto"}>
            <Text color={"whiteC.100"} fontWeight={"600"} fontSize={"sm"}>
              Follow Us :
            </Text>
            <Flex gap={4}>
              <Link
                as={RouterLink}
                target="_blank"
                rel="noopener noreferrer"
                to="https://www.instagram.com/bulletscoutofficial/"
              >
                <InstagramIcon color={"whiteC.100"} />
              </Link>
              <Link
                as={RouterLink}
                target="_blank"
                rel="noopener noreferrer"
                to="https://www.youtube.com/@BulletScoutOfficial"
              >
                <YouTubeIcon color={"whiteC.100"} />
              </Link>
              <Link
                as={RouterLink}
                target="_blank"
                rel="noopener noreferrer"
                to="https://www.facebook.com/BulletScoutOfficial"
              >
                <FacebookIcon color={"whiteC.100"} />
              </Link>
              <Link
                as={RouterLink}
                target="_blank"
                rel="noopener noreferrer"
                to="https://x.com/BulletScoutCom"
              >
                <TwitterIcon color={"whiteC.100"} />
              </Link>
            </Flex>
          </Flex>
        </Flex>
        <Flex
          gap={{ xl: 28, lg: 20, base: 13 }}
          w={{ base: "100%", md: "48%", lg: "auto" }}
          justifyContent={"space-between"}
          mb={{ base: 10, md: 0 }}
        >
          <Box>
            <Heading
              as={"h3"}
              fontSize={"md"}
              color={"whiteC.100"}
              fontWeight={"500"}
              mb={5}
            >
              Quick Links
            </Heading>
            <List display={"flex"} flexDirection={"column"} gap={"10px"}>
              {quickLinks.map((link) => (
                <ListItem key={link.name}>
                  <Link
                    as={RouterLink}
                    to={
                      location.pathname === "/"
                        ? link.link
                        : link.link === "#contact"
                          ? `/${link.link}`
                          : link.link
                    }
                    fontSize={"sm"}
                    color={getCurrentColor(
                      colorMode,
                      "whiteC.100",
                      "grayC.1600"
                    )}
                    _hover={{ textDecoration: "none" }}
                  >
                    {link.name}
                  </Link>
                </ListItem>
              ))}
            </List>
          </Box>

          <Box>
            <Heading
              as={"h3"}
              fontSize={"md"}
              color={"whiteC.100"}
              fontWeight={"500"}
              mb={5}
            >
              Types of Ammo
            </Heading>
            <List display={"flex"} flexDirection={"column"} gap={"10px"}>
              {typeOfAmmo.map((type) => {
                // Generate SEO URL for footer category links
                const seoUrl = mappingsReady && facetMappings 
                  ? generateSeoCategoryLink(type.name, facetMappings)
                  : type.link;
                
                return (
                  <ListItem key={type.name}>
                    <Link
                      as={RouterLink}
                      to={seoUrl}
                      fontSize={"sm"}
                      _hover={{ textDecoration: "none" }}
                      color={getCurrentColor(
                        colorMode,
                        "whiteC.100",
                        "grayC.1600"
                      )}
                    >
                      {type.name}
                    </Link>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </Flex>

        <Flex flexDirection={"column"}>
          <Heading
            as={"h3"}
            fontSize={"md"}
            color={"whiteC.100"}
            fontWeight={"500"}
            mb={5}
          >
            Contact Us
          </Heading>
          <Flex gap={6} flexDirection="column">
            <Image
              w={"250px"}
              src={colorMode === "light" ? emailContact : emailContactDark}
            />
            <Link
              as={RouterLink}
              to="/terms-and-privacy"
              fontSize={"sm"}
              color={getCurrentColor(colorMode, "whiteC.100", "grayC.1600")}
              _hover={{ textDecoration: "none" }}
            >
              Terms of Use and Privacy Policy
            </Link>
          </Flex>
        </Flex>
      </Flex>
      <Flex
        py={3}
        w={"100%"}
        justifyContent={"center"}
        alignItems={"center"}
        bg={getCurrentColor(colorMode, "whiteC.200", "blueC.500")}
        fontSize={"sm"}
        color={getCurrentColor(colorMode, "grayC.1900", "grayC.2300")}
      >
        <CIcon /> {new Date().getFullYear()} Bullet Scout. All Rights Reserved
      </Flex>
    </Box>
  );
};
export default Footer;
